import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Editor } from '@tinymce/tinymce-react';
import { faNewspaper, faPlus, faSave, faTimes } from '@fortawesome/pro-solid-svg-icons';

import PageTemplate from '../../Components/Templates/Page/Page/PageTemplate';
import PageHeader from '../../Components/Organisms/PageHeader/PageHeader';
import Breadcrumbs from '../../Components/Molecules/Breadcrumbs/Breadcrumbs';
import BreadcrumbItem from '../../Components/Atoms/BreadcrumbItem/BreadcrumbItem';
import Container from '../../Components/Atoms/Container/Container';
import FormGroup from '../../Components/Molecules/Form/FormGroup/FormGroup';
import TextField from '../../Components/Organisms/TextField/TextField';
import Button from '../../Components/Atoms/Button/Button';
import Notification from '../../Components/Atoms/Notification/Notification';
import FileUpload from '../../Components/Atoms/FileUpload/FileUpload';
import FontAwesomeIcon from '../../Components/Atoms/FontAwesomeIcon/FontAwesomeIcon';

import { BlogService } from '../../Services/Blog/BlogService';
import { BlogPost, BlogCategory } from '../../Api/Modules/Blog';
import { ROUTES } from 'Services/Route/Route.types';
import { UploadService } from '../../Services/Upload/UploadService';

const BlogEdit: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const blogService = BlogService.getInstance();
  const uploadService = UploadService.getInstance();
  
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    title: '',
    excerpt: '',
    content: '',
    imageUrl: '',
    slug: '',
    category_id: '',
    isPublished: false
  });

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setInitialLoading(true);
      setError(null);

      const [post, categoriesData] = await Promise.all([
        blogService.getBlogPost(id!),
        blogService.getCategories()
      ]);

      console.log('📝 BlogEdit: Ruwe post data:', post);
      console.log('📝 BlogEdit: Ruwe categories data:', categoriesData);

      // Add this log to check if post.category_id exists in categoriesData
        const categoryExists = categoriesData.some(cat => cat.id === post.category_id);
        console.log(`📝 BlogEdit: Post category_id (${post.category_id}) exists in categories: ${categoryExists}`);

      setFormData({
        title: post.title || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        imageUrl: post.imageUrl || '',
        slug: post.slug || '',
        category_id: post.category_id || '',
        isPublished: post.isPublished || false
      });

      setCategories(categoriesData);
    } catch (err) {
      console.error('❌ BlogEdit: Error bij laden data', err);
      setError('Blog artikel laden mislukt.');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Auto-generate slug from title
    if (field === 'title') {
      const slug = value.toString()
        .toLowerCase()
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      
      setFormData(prev => ({
        ...prev,
        slug: slug
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      setError('Titel is verplicht');
      return;
    }

    if (!formData.category_id) {
      setError('Categorie is verplicht');
      return;
    }

    if (!formData.content.trim()) {
      setError('Inhoud is verplicht');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const updatedPost: Partial<BlogPost> = {
        title: formData.title.trim(),
        excerpt: formData.excerpt.trim(),
        content: formData.content,
        imageUrl: formData.imageUrl.trim(),
        slug: formData.slug,
        category_id: formData.category_id,
        isPublished: formData.isPublished
      };

      await blogService.updateBlogPost(id!, updatedPost);
      navigate('/blog');
    } catch (err) {
      console.error('❌ BlogEdit: Error bij bijwerken post', err);
      setError('Blog artikel bijwerken mislukt.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/blog');
  };

  if (initialLoading) {
    return (
      <PageTemplate>
        <Container padding='vertical'>
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <div style={{ 
              display: 'inline-block',
              width: '32px',
              height: '32px',
              border: '3px solid #e5e7eb',
              borderTop: '3px solid #2563eb',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }}></div>
            <p style={{ marginTop: '16px', color: '#6b7280' }}>Blog artikel yükleniyor...</p>
          </div>
        </Container>
      </PageTemplate>
    );
  }

  return (
    <PageTemplate>
      <Helmet>
        <title>{ ROUTES.BLOG.title }</title>
      </Helmet>
      
      <PageHeader
        breadcrumbs={
          <Breadcrumbs>
            <BreadcrumbItem size='small' path={ROUTES.DASHBOARD.path}>Dashboard</BreadcrumbItem>
            <BreadcrumbItem size='small' path={ROUTES.BLOG.path}>Blog</BreadcrumbItem>
            <BreadcrumbItem size='small'>Artikel Bewerken</BreadcrumbItem>
          </Breadcrumbs>
        }
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{ 
            width: '48px', 
            height: '48px', 
            backgroundColor: '#dbeafe', 
            borderRadius: '8px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center' 
          }}>
            <FontAwesomeIcon icon={faNewspaper} size="lg" color="#2563eb" />
          </div>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
              Blog Artikel Bewerken
            </h1>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '4px 0 0 0' }}>
              Blog artikel bewerken
            </p>
          </div>
        </div>
      </PageHeader>

      <Container padding='vertical'>
        {error && (
          <Notification variant='error' style={{ marginBottom: '24px' }}>
            {error}
          </Notification>
        )}

        <div style={{ backgroundColor: 'white', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '24px' }}>
          <div style={{ display: 'grid', gap: '24px' }}>
            {/* Title */}
            <FormGroup title="Titel" isRequired>
              <TextField
                isEdit={true}
                value={formData.title}
                onChange={(value) => handleInputChange('title', value)}
                placeholder="Voer de titel van het artikel in"
              />
            </FormGroup>

            {/* Slug */}
            <FormGroup title="Slug" isRequired>
              <TextField
                isEdit={true}
                value={formData.slug}
                onChange={(value) => handleInputChange('slug', value)}
                placeholder="artikel-slug"
              />
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                URL-vriendelijke versie van de titel (automatisch gegenereerd)
              </p>
            </FormGroup>

            {/* Category */}
            <FormGroup title="Categorie" isRequired>
              <select
                value={formData.category_id}
                onChange={(e) => handleInputChange('category_id', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Selecteer een categorie</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </FormGroup>

            {/* Excerpt */}
            <FormGroup title="Korte omschrijving">
              <TextField
                isEdit={true}
                value={formData.excerpt}
                onChange={(value) => handleInputChange('excerpt', value)}
                placeholder="Plaats hier een korte omschrijving."
              />
            </FormGroup>

            {/* Content */}
            <FormGroup title="Inhoud" isRequired>
              <div className="border border-gray-300 rounded-md">
                <Editor
                  apiKey='jj93h9sj60y0jobenpdq5gr9pkl5ld3if1xpv62qgzxdfvn2'
                  value={formData.content}
                  init={{
                    height: 400,
                    menubar: false,
                    plugins: [
                      'advlist',
                      'autolink',
                      'lists',
                      'link',
                      'image',
                      'charmap',
                      'print',
                      'preview',
                      'anchor',
                      'searchreplace',
                      'visualblocks',
                      'code',
                      'fullscreen',
                      'insertdatetime',
                      'media',
                      'table',
                      'paste',
                      'code',
                      'help',
                      'wordcount'
                    ],
                    toolbar: 'undo redo | styles | bold italic underline | bullist numlist | alignleft aligncenter alignright | link image | removeformat code',
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                    images_upload_url: '/api/v2/upload/image',
                    images_upload_handler: async (blobInfo, progress) => {
                      try {
                        console.log('📤 TinyMCE: Starting image upload', blobInfo);
                        
                        // Blob'u File objesine çevir
                        const file = new File([blobInfo.blob()], blobInfo.filename(), { type: blobInfo.blob().type });
                        
                        // UploadService kullanarak yükle
                        const response = await uploadService.uploadImage(file);
                        
                        console.log('📤 TinyMCE: Upload successful', response);
                        return response.url; // path'i döndür
                      } catch (error) {
                        console.error('❌ TinyMCE: Upload failed', error);
                        throw new Error('Afbeelding uploaden mislukt');
                      }
                    }
                  }}
                  onEditorChange={(newValue) => handleInputChange('content', newValue)}
                />
              </div>
            </FormGroup>

            {/* Image Upload */}
            <FormGroup title="Blog Afbeelding">
              <FileUpload
                value={formData.imageUrl}
                onChange={(value) => handleInputChange('imageUrl', value)}
                placeholder="Selecteer een blog afbeelding of sleep het hierheen"
                accept="image/*"
                maxSize={5}
              />
            </FormGroup>

            {/* Published Status */}
            <FormGroup title="Publicatie Status">
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  id="isPublished"
                  checked={formData.isPublished}
                  onChange={(e) => handleInputChange('isPublished', e.target.checked)}
                  style={{
                    width: '16px',
                    height: '16px',
                    marginRight: '8px'
                  }}
                />
                <label htmlFor="isPublished" style={{ fontSize: '14px', color: '#374151' }}>
                  Direct publiceren
                </label>
              </div>
            </FormGroup>
          </div>

          {/* Form Actions */}
          <div style={{ 
            marginTop: '32px', 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '12px' 
          }}>
            <Button
              variant='border'
              color='neutral-dark'
              size='medium'
              onClick={handleCancel}
              iconBefore={<FontAwesomeIcon icon={faTimes} />}
            >
              Annuleren
            </Button>
            <Button
              variant='solid'
              color='primary'
              size='medium'
              onClick={handleSubmit}
              isDisabled={loading}
              isLoading={loading}
              iconBefore={!loading && <FontAwesomeIcon icon={faSave} />}
            >
              {loading ? 'Bijwerken...' : 'Artikel Bijwerken'}
            </Button>
          </div>
        </div>
      </Container>
    </PageTemplate>
  );
};

export default BlogEdit; 